import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Zap, CheckCircle, ArrowRight, Inbox, Star, Users,
  LayoutGrid, Clock, Sparkles, Check, Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useI18n } from '../i18n';

export default function LandingPage() {
  const { t } = useI18n();

  const FEATURES = [
    { icon: Inbox, title: t('featCaptureTitle'), description: t('featCaptureDesc') },
    { icon: LayoutGrid, title: t('featKanbanTitle'), description: t('featKanbanDesc') },
    { icon: Star, title: t('featFocusTitle'), description: t('featFocusDesc') },
    { icon: Users, title: t('featCollabTitle'), description: t('featCollabDesc') },
    { icon: Clock, title: t('featTrackTitle'), description: t('featTrackDesc') },
    { icon: Sparkles, title: t('featAITitle'), description: t('featAIDesc'), pro: true },
  ];

  const BENEFITS = [t('benefit1'), t('benefit2'), t('benefit3'), t('benefit4'), t('benefit5')];

  const FREE_FEATURES = [t('feat1Workspace'), t('feat2Invites'), t('featKanban'), t('featQuickCapture'), t('featTags'), t('featFocus')];
  const PRO_FEATURES = [t('featUnlimitedWs'), t('featUnlimitedInv'), t('featAI'), t('featBreakdown'), t('featPlanning'), t('featAllFree')];

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-50">
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
              <Zap className="w-4 h-4 text-black" />
            </div>
            <span className="font-semibold font-['Outfit'] text-lg" data-testid="brand-name">FlowFocus</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <a href="#pricing" className="hidden sm:block text-sm text-zinc-400 hover:text-zinc-100 transition-colors">
              {t('pricing')}
            </a>
            <Link to="/login" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">
              {t('login')}
            </Link>
            <Link to="/register" className="hidden sm:block">
              <Button className="bg-zinc-100 text-zinc-900 hover:bg-white text-sm h-9">
                {t('startFree')}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-8 sm:pb-12">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-xs mb-6">
            <Sparkles className="w-3 h-3" />
            {t('landingTagline')}
          </div>
          
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] tracking-tight leading-[1.1] mb-6">
            {t('landingH1_1')}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">{t('landingH1_2')}</span>
          </h1>
          
          <p className="text-sm sm:text-lg text-zinc-400 mb-8 leading-relaxed max-w-2xl mx-auto px-2">
            {t('landingSubtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 px-4 sm:px-0">
            <Link to="/register">
              <Button className="w-full sm:w-auto bg-zinc-100 text-zinc-900 hover:bg-white h-12 px-8 text-base font-medium">
                {t('startFree')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="#pricing">
              <Button variant="outline" className="w-full sm:w-auto border-white/10 text-zinc-300 hover:bg-white/5 h-12 px-8 text-base">
                {t('seePricing')}
              </Button>
            </a>
          </div>

          <div className="flex flex-wrap gap-x-4 sm:gap-x-6 gap-y-2 justify-center">
            {BENEFITS.map((benefit, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-zinc-500">
                <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500 flex-shrink-0" />
                {benefit}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* iMac Mockup */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
        <div className="relative" data-testid="imac-mockup">
          <div className="bg-[#1a1a1e] rounded-xl sm:rounded-2xl border border-white/10 overflow-hidden shadow-2xl shadow-black/50">
            <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#111113] border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-red-500/60"></div>
                <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-yellow-500/60"></div>
                <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-green-500/60"></div>
              </div>
              <div className="flex-1 flex justify-center">
                <div className="px-4 sm:px-8 py-1 rounded-md bg-white/5 text-[9px] sm:text-[10px] text-zinc-500">
                  flowfocus.se
                </div>
              </div>
            </div>
            <div className="flex h-[200px] sm:h-[320px] lg:h-[420px] overflow-hidden">
              {/* Sidebar mock */}
              <div className="w-36 sm:w-52 bg-[#09090B] border-r border-white/5 p-3 sm:p-4 flex-shrink-0 hidden sm:block">
                <div className="flex items-center gap-2 p-2 rounded-lg bg-white/5 mb-4 sm:mb-6">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-zinc-700"></div>
                  <div>
                    <div className="text-[9px] sm:text-[10px] text-zinc-100">Mitt Workspace</div>
                    <div className="text-[7px] sm:text-[8px] text-zinc-500">{t('personal')}</div>
                  </div>
                </div>
                <div className="space-y-1">
                  {[t('all'), t('inbox'), t('today'), t('starred'), t('done')].map((item, i) => (
                    <div key={i} className={`flex items-center gap-2 px-2 py-1 sm:py-1.5 rounded text-[9px] sm:text-[10px] ${i === 0 ? 'bg-white/5 text-zinc-100' : 'text-zinc-500'}`}>
                      <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-zinc-700"></div>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
              {/* Main mock */}
              <div className="flex-1 bg-[#09090B] p-3 sm:p-6 overflow-hidden">
                <div className="text-xs sm:text-sm font-bold text-zinc-100 font-['Outfit'] mb-1">Mitt Workspace</div>
                <div className="text-[8px] sm:text-[9px] text-zinc-500 mb-3 sm:mb-4">{t('allTasks')}</div>
                <div className="h-7 sm:h-9 rounded-lg bg-[#141416] border border-white/5 flex items-center px-2 sm:px-3 mb-3 sm:mb-4">
                  <span className="text-[9px] sm:text-[10px] text-zinc-600">+ {t('quickCapturePlaceholder')}</span>
                </div>
                <div className="flex gap-2 sm:gap-3">
                  {[
                    { name: t('inbox'), color: 'bg-zinc-500', tasks: ['Boka möte', 'Research'] },
                    { name: t('nextUp'), color: 'bg-blue-500', tasks: ['Design'] },
                    { name: t('inProgress'), color: 'bg-yellow-500', tasks: ['Blogg', 'API fix'] },
                    { name: 'Done', color: 'bg-emerald-500', tasks: ['Offert'] },
                  ].map((col, i) => (
                    <div key={i} className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 mb-1.5 sm:mb-2">
                        <div className={`w-1.5 h-1.5 rounded-full ${col.color}`}></div>
                        <span className="text-[8px] sm:text-[9px] text-zinc-400 truncate">{col.name}</span>
                      </div>
                      <div className="space-y-1 sm:space-y-1.5">
                        {col.tasks.map((task, j) => (
                          <div key={j} className="p-1.5 sm:p-2 rounded-lg bg-[#141416] border border-white/5">
                            <div className="text-[8px] sm:text-[9px] text-zinc-200 truncate">{task}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="hidden sm:flex justify-center">
            <div className="w-24 h-6 bg-gradient-to-b from-zinc-700 to-zinc-800 rounded-b-lg"></div>
          </div>
          <div className="hidden sm:flex justify-center -mt-0.5">
            <div className="w-40 h-2 bg-zinc-800 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-white/5 bg-[#0A0A0C]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Outfit'] mb-4">{t('featuresTitle')}</h2>
            <p className="text-zinc-500 max-w-xl mx-auto text-sm">{t('featuresSubtitle')}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {FEATURES.map((feature, idx) => (
              <div key={idx} className="p-5 sm:p-6 rounded-xl bg-[#111113] border border-white/5 hover:border-white/10 transition-colors relative">
                {feature.pro && (
                  <span className="absolute top-4 right-4 px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 text-[10px] font-medium border border-yellow-500/20">PRO</span>
                )}
                <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center mb-4">
                  <feature.icon className="w-5 h-5 text-zinc-300" />
                </div>
                <h3 className="font-medium text-zinc-100 mb-2">{feature.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Outfit'] mb-4">{t('howTitle')}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {[
              { n: '1', title: t('step1'), desc: t('step1Desc') },
              { n: '2', title: t('step2'), desc: t('step2Desc') },
              { n: '3', title: t('step3'), desc: t('step3Desc') },
              { n: '4', title: t('step4'), desc: t('step4Desc') },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-black font-bold text-base sm:text-lg mx-auto mb-3 sm:mb-4">{s.n}</div>
                <h3 className="font-medium text-zinc-100 mb-1 sm:mb-2 text-sm sm:text-base">{s.title}</h3>
                <p className="text-xs sm:text-sm text-zinc-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-white/5 bg-[#0A0A0C]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Outfit'] mb-4">{t('pricingTitle')}</h2>
            <p className="text-zinc-500 text-sm">{t('pricingSubtitle')}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
            {/* Free */}
            <div className="p-5 sm:p-6 rounded-2xl bg-[#111113] border border-white/5" data-testid="pricing-free">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-zinc-400" />
                <h3 className="text-lg font-semibold text-zinc-100 font-['Outfit']">{t('free')}</h3>
              </div>
              <p className="text-3xl font-bold text-zinc-100 font-['Outfit'] mb-1">0 kr<span className="text-sm text-zinc-500 font-normal">{t('perMonth')}</span></p>
              <p className="text-xs text-zinc-500 mb-6">{t('freeDesc')}</p>
              <ul className="space-y-3 mb-8">
                {FREE_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-400">
                    <Check className="w-4 h-4 text-zinc-600 mt-0.5 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <Link to="/register"><Button variant="outline" className="w-full border-white/10 text-zinc-300 hover:bg-white/5 h-11">{t('startFree')}</Button></Link>
            </div>
            {/* Pro */}
            <div className="p-5 sm:p-6 rounded-2xl bg-gradient-to-b from-yellow-500/[0.04] to-orange-500/[0.04] border border-yellow-500/20 relative" data-testid="pricing-pro">
              <div className="absolute -top-3 right-4 px-3 py-1 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-xs font-bold text-black">{t('popular')}</div>
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-5 h-5 text-yellow-400" />
                <h3 className="text-lg font-semibold text-yellow-400 font-['Outfit']">{t('pro')}</h3>
              </div>
              <p className="text-3xl font-bold text-zinc-100 font-['Outfit'] mb-1">199 kr<span className="text-sm text-zinc-500 font-normal">{t('perMonth')}</span></p>
              <p className="text-xs text-zinc-500 mb-6">{t('proDesc')}</p>
              <ul className="space-y-3 mb-8">
                {PRO_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-200">
                    <Check className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <Link to="/register"><Button className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:from-yellow-300 hover:to-orange-400 h-11 font-semibold">{t('startWithPro')}<ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Outfit'] mb-4">{t('ctaTitle')}</h2>
          <p className="text-zinc-500 mb-8 max-w-md mx-auto text-sm">{t('ctaSubtitle')}</p>
          <Link to="/register"><Button className="bg-zinc-100 text-zinc-900 hover:bg-white h-12 px-8 text-base font-medium">{t('createAccountFree')}<ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <Zap className="w-4 h-4" /><span>FlowFocus</span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <p className="text-zinc-600 text-sm">© {new Date().getFullYear()} FlowFocus</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
